import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';

type QuestionType = 'checkbox' |
    'yesno' |
    'textarea' |
    'text-field' |
    'email-field' |
    'month-field' |
    'number-field' |
    'tel-field' |
    'radio-select' |
    'dropdown-select' |
    'checkbox-select';

type ValueType<T extends QuestionType> =
    T extends 'checkbox-select'
    ? string[]
    : T extends 'text-field' | 'textarea' | 'email-field' | 'tel-field' | 'month-field' | 'dropdown-select' | 'radio-select'
    ? string
    : T extends 'checkbox' | 'yesno'
    ? boolean
    : T extends 'number-field'
    ? number
    : never;

@Component({
    selector: 'ell-survey-question',
    standalone: true,
    imports: [CommonModule, FormsModule],
    templateUrl: './survey-question.component.html',
    styleUrl: './survey-question.component.scss'
})
export class SurveyQuestionComponent<T extends QuestionType> {
    get typed(): QuestionType | undefined {
        return this.type;
    }

    /**
     * When toggling, this allows the filled in content to reappear
     */
    private otherFieldContent?: ValueType<T>;

    get max() {
        if (this.type === 'month-field') {
            const date = new Date();
            let month = `${date.getMonth() + 1}`;
            if (month.length === 1) {
                // needs to be in yyyy-MM format
                month = `0${month}`;
            }
            return `${date.getFullYear()}-${month}`;
        }

        return undefined;
    }

    get min() {
        if (this.type === 'number-field') {
            return 0;
        }

        return undefined;
    }

    showOtherField = false;

    @Input()
    name!: string;

    @Input()
    type!: T;

    /**
     * The last option is an other field
     */
    @Input()
    other: boolean = false;

    @Input()
    question?: string;

    @Input()
    options?: string[];

    @Input()
    answer?: ValueType<T>;

    @Output()
    answerChange = new EventEmitter<ValueType<T>>();

    emit(value: any) {
        this.answerChange.emit(value);
    }

    /**
     * Gets the open field value or undefined if none
     * @param answers the answers to look through
     * @returns
     */
    private filledInOpenField(answers: string[]): string | undefined {
        // any answer given which IS NOT an option?
        for (const answer of answers) {
            if (this.options!.indexOf(answer) === -1) {
                // unknown answer, so we must assume that this is the open field
                return answer;
            }
        }

        return undefined;
    }

    /**
     * This is for checking a checkbox-select
     * @param answer
     */
    hasSelectedOption(answer: string, last: boolean) {
        const answers = <string[]>this.answer ?? [];
        const selected = answers.indexOf(answer) >= 0;
        if (selected) {
            return true;
        }

        if (last) {
            if (this.filledInOpenField(answers) !== undefined) {
                return true;
            }
        }

        return false;
    }

    emitCheckbox(event: Event, last: boolean) {
        const checkbox = <HTMLInputElement>event.target;
        // given answers
        const answers = <string[]>(this.answer ?? []);
        // value of this checkbox
        const value = <string>checkbox.value;
        // we know it is a checkbox-select so string[] is the type;
        let updated: string[] | undefined = undefined;

        // is the answer already given?
        let givenAnswer: string | undefined = undefined;
        if (answers.indexOf(value) >= 0) {
            // answer is in the given answers
            givenAnswer = value;
        }
        else if (last && this.other) {
            // if an open field is associated with this, then check for
            // an open field in the given answers
            givenAnswer = this.filledInOpenField(answers);
        }

        if (checkbox.checked) {
            if (givenAnswer === undefined) {
                // add it to the selection
                updated = [...answers, value];
            }
        } else {
            if (givenAnswer !== undefined) {
                // remove it from the selection
                updated = answers.filter(a => a !== givenAnswer);
            }
        }

        if (updated) {
            // we know it is a checkbox-select so string[] is the type
            this.answerChange.emit(<ValueType<T>>updated);
        }
    }

    emitDropdown(event: Event) {
        const radio = <HTMLSelectElement>event.target;
        if (radio.value) {
            this.answerChange.emit(<any>radio.value);
        }

        if (this.other) {
            // select last option
            const selectedLast = radio.value === this.options![this.options!.length - 1];

            this.showOtherField = selectedLast;
            if (selectedLast && this.otherFieldContent) {
                // restore the previous answer
                this.answerChange.emit(this.otherFieldContent);
            }
        }
    }

    emitRadio(event: Event, showOtherField = false) {
        const radio = <HTMLInputElement>event.target;
        if (radio.checked) {
            this.answerChange.emit(<any>radio.value);
        }

        if (this.other) {
            this.showOtherField = showOtherField;
            if (showOtherField && this.otherFieldContent) {
                // restore the previous answer
                this.answerChange.emit(this.otherFieldContent);
            }
        }
    }

    emitField(event: Event) {
        const value = <any>(<HTMLInputElement | HTMLSelectElement>event.target).value;
        if (this.showOtherField) {
            this.otherFieldContent = value;
        }
        this.answerChange.emit(value);
    }
}
